import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import {
  confirmPasswordReset,
  verifyPasswordResetCode,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { auth } from "../firebase";
import logoImg from "../images/mykyc-logo-login-lightblue-1000.png";
import logoGrey from "../images/mykyc-logo-grey-1000.png";
import timerExpAnim from "../images/timer-exp-b.json";
import { MAIL_API_BASE } from "../config/api";
import "./css/ActivateAccountPage.css";

function validatePassword(pw) {
  return {
    length:    pw.length >= 8,
    uppercase: /[A-Z]/.test(pw),
    special:   /[^A-Za-z0-9\s]/.test(pw),
    numeric:   /[0-9]/.test(pw),
  };
}

function removePasswordWhitespace(value) {
  return String(value || "").replace(/\s/g, "");
}


export default function ActivateAccountPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();

  const oobCode = params.get("oobCode") || "";
  const mode = params.get("mode") || "";
  const isReset = mode === "resetPassword";
  const accountName = params.get("name") || "";

  const [email, setEmail] = useState("");
  const [checking, setChecking] = useState(true);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      setErr("");
      setOk("");

      if (!oobCode) {
        setChecking(false);
        setErr("Invalid or missing activation code.");
        return;
      }

      try {
        const mail = await verifyPasswordResetCode(auth, oobCode);
        if (!cancelled) setEmail(mail);
      } catch (e) {
        if (!cancelled)
          setErr(
            isReset
              ? "Reset link expired or already used."
              : "Activation link expired or already used."
          );
      } finally {
        if (!cancelled) setChecking(false);
      }
    }

    run();
    return () => (cancelled = true);
  }, [oobCode, isReset]);

  const rules = validatePassword(pw1);
  const allRulesMet = rules.length && rules.uppercase && rules.special && rules.numeric;
  const passwordsMatch = pw2.length > 0 && pw1 === pw2;
  const canTrySubmit = allRulesMet && pw2.length > 0;
  const canSubmit = allRulesMet && passwordsMatch;
  const confirmPrefixMismatch = pw2.length > 0 && !pw1.startsWith(pw2);
  const showConfirmMismatch =
    confirmPrefixMismatch || (submitAttempted && pw2.length > 0 && !passwordsMatch);

  async function handleSetPassword(e) {
    e.preventDefault();
    if (saving || !canTrySubmit) return;

    setSubmitAttempted(true);
    if (!passwordsMatch) return;

    setErr("");
    setOk("");
    setSaving(true);

    try {
      await confirmPasswordReset(auth, oobCode, pw1);
      await signInWithEmailAndPassword(auth, email, pw1);

      if (!isReset) {
        const token = await auth.currentUser.getIdToken(true);
        const resp = await fetch(
          `${MAIL_API_BASE}/auth/mark-onboarded`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok || json?.ok === false) {
          throw new Error(json?.error || "Failed to complete onboarding.");
        }
      }

      setOk("done");
      setTimeout(() => nav("/clients", { replace: true }), 1400);
    } catch (e) {
      setErr(e?.message || "Failed to set password.");
    } finally {
      setSaving(false);
    }
  }

  // Full-page loading state — shown while verifying the link
  if (checking) {
    return (
      <div className="activatePage">
        <div className="activateCheckingWrap">
          <div className="activateDotsLoader">
            <div className="activateDot" />
            <div className="activateDot" />
            <div className="activateDot" />
          </div>
        </div>
      </div>
    );
  }

  // Full-page expired/invalid state
  if (err && !email) {
    return (
      <div className="docUploadExpiredPage" style={{ background: "#E4EBF0" }}>
        <div className="docUploadExpiredShell">
          <div className="docUploadExpiredContent">
            <motion.div
              className="docUploadExpiredHeader"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="docUploadExpiredBadge">
                {isReset ? "Reset · Link expired" : "Activate · Link expired"}
              </div>
              <Lottie
                animationData={timerExpAnim}
                loop={true}
                lottieRef={(ref) => ref?.setSpeed(0.5)}
                className="docUploadExpiredLottie"
                aria-hidden="true"
              />
              <motion.h1
                className="docUploadExpiredTitle"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.38, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                This link can't be used
              </motion.h1>
              <motion.p
                className="docUploadExpiredLead"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.38, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
              >
                {isReset
                  ? "Your password reset link has expired or has already been used."
                  : "Your activation link has expired or has already been used."}
              </motion.p>
            </motion.div>

            <motion.div
              className="docUploadExpiredCard"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="docUploadExpiredCardTitle">What to do next</div>
              <div className="docUploadExpiredCardItem">
                <span className="docUploadExpiredCardDot" aria-hidden="true" />
                <span>{isReset ? "Please contact myKYC team and request a new password reset link." : "Please contact myKYC team and request a new activation link."}</span>
              </div>
              <div className="docUploadExpiredCardItem">
                <span className="docUploadExpiredCardDot" aria-hidden="true" />
                <span>If you believe this is an error, you can try refreshing the page.</span>
              </div>
              <div className="docUploadExpiredActions">
                <button
                  type="button"
                  className="btn docUploadExpiredRetryBtn"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </button>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="docUploadFooterBrand"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <img src={logoGrey} alt="myKYC" className="docUploadFooterLogo" />
          </motion.div>
        </div>
      </div>
    );
  }

  const headTitle = ok ? "All done!" : (isReset ? "Change your password" : "Activate your account");
  const headSub = ok ? "Redirecting you to your dashboard…" : (isReset ? "Enter your new password for myKYC" : "Choose a secure password for myKYC");

  return (
    <div className="activatePage">
      <div className="activateLogoCircle">
        <img src={logoImg} alt="myKYC" className="activateLogo" />
      </div>

      <motion.div
        className="activateCard"
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          default: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.18 },
          layout:  { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
        }}
      >
        <div className="activateCardHead">
          <div className="activateHeadTitle">{headTitle}</div>
          <div className="activateHeadSub">{headSub}</div>
        </div>

        <div className="activateCardBody">

          {/* Form */}
          {!checking && !ok && email && (
            <>
              {accountName && (
                <div className="activateWelcome">
                  Welcome, {accountName.split(" ")[0]}
                </div>
              )}

              <div className="activateEmailChip">
                <i className="bi bi-envelope" aria-hidden="true" />
                <span>{email}</span>
              </div>

              <form onSubmit={handleSetPassword} noValidate>
                <div className="activateFieldWrap">

                  {/* Password field */}
                  <div className="activateField">
                    <label className="activateLabel">New password</label>
                    <div className="activateInputWrap">
                      <input
                        className="activateInput"
                        type={showPw1 ? "text" : "password"}
                        placeholder={isReset ? "Enter new password" : "Create password"}
                        value={pw1}
                        onChange={(e) => {
                          setPw1(removePasswordWhitespace(e.target.value));
                          setSubmitAttempted(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === " ") e.preventDefault();
                        }}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="activateInputReveal"
                        onClick={() => setShowPw1((v) => !v)}
                        tabIndex={-1}
                        aria-label={showPw1 ? "Hide password" : "Show password"}
                      >
                        <i className={showPw1 ? "bi bi-eye-slash" : "bi bi-eye"} aria-hidden="true" />
                      </button>
                    </div>
                    <AnimatePresence initial={false}>
                      {pw1.length > 0 && (
                        <motion.div
                          key="pw1-rules"
                          initial={{ height: 0 }}
                          animate={{ height: "auto" }}
                          exit={{ height: 0 }}
                          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                          style={{ overflow: "hidden" }}
                        >
                          <motion.div
                            className="activateValidation"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.18, delay: 0.22 }}
                          >
                            <div className={`activateValidationItem ${rules.length ? "is-ok" : "is-err"}`}>
                              <i className={rules.length ? "bi bi-check" : "bi bi-x"} />
                              At least 8 characters
                            </div>
                            <div className={`activateValidationItem ${rules.uppercase ? "is-ok" : "is-err"}`}>
                              <i className={rules.uppercase ? "bi bi-check" : "bi bi-x"} />
                              One uppercase letter
                            </div>
                            <div className={`activateValidationItem ${rules.special ? "is-ok" : "is-err"}`}>
                              <i className={rules.special ? "bi bi-check" : "bi bi-x"} />
                              One special character
                            </div>
                            <div className={`activateValidationItem ${rules.numeric ? "is-ok" : "is-err"}`}>
                              <i className={rules.numeric ? "bi bi-check" : "bi bi-x"} />
                              One numeric character
                            </div>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Confirm field */}
                  <div className="activateField activateConfirmField">
                    <label className="activateLabel">Confirm password</label>
                    <div className="activateInputWrap">
                      <input
                        className="activateInput"
                        type={showPw2 ? "text" : "password"}
                        placeholder="Repeat your password"
                        value={pw2}
                        onChange={(e) => {
                          setPw2(removePasswordWhitespace(e.target.value));
                          setSubmitAttempted(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === " ") e.preventDefault();
                        }}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="activateInputReveal"
                        onClick={() => setShowPw2((v) => !v)}
                        tabIndex={-1}
                        aria-label={showPw2 ? "Hide password" : "Show password"}
                      >
                        <i className={showPw2 ? "bi bi-eye-slash" : "bi bi-eye"} aria-hidden="true" />
                      </button>
                    </div>
                    <AnimatePresence initial={false}>
                      {showConfirmMismatch && (
                        <motion.div
                          key="pw2-match"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                          style={{ overflow: "hidden" }}
                        >
                          <motion.div
                            className="activateMatchMsg is-err"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.18, delay: 0.22 }}
                          >
                            <i className="bi bi-x" aria-hidden="true" />
                            Passwords do not match
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                </div>

                <button
                  className="activateBtn"
                  type="submit"
                  disabled={saving || !canSubmit}
                >
                  {saving
                    ? "Please wait…"
                    : isReset ? "Update password" : "Activate"}
                </button>
              </form>
            </>
          )}

          {/* Success */}
          {ok && (
            <div className="activateSuccessWrap">
              <div className="activateSuccessIcon">
                <i className="bi bi-check-lg" aria-hidden="true" />
              </div>
              <div className="activateSuccessTitle">
                {isReset ? "Password updated!" : "Account activated!"}
              </div>
              <div className="activateSuccessSub">
                Redirecting to your dashboard…
              </div>
            </div>
          )}

        </div>
      </motion.div>

      <div className="activateFooter">
        © {new Date().getFullYear()} Naxero Digital Technology
      </div>
    </div>
  );
}
